import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  TrendingUp, TrendingDown, RefreshCw, ArrowUpDown, ExternalLink,
  Filter, Download, CheckCircle2, Circle, Receipt,
} from "lucide-react";
import { fmtEUR } from "@/lib/format";


const ESTADO_COLORS = {
  borrador: "bg-zinc-700 text-zinc-200",
  en_curso: "bg-blue-600/25 text-blue-200 border border-blue-500/40",
  presupuestada: "bg-amber-600/25 text-amber-200 border border-amber-500/40",
  terminado: "bg-emerald-600/25 text-emerald-200 border border-emerald-500/40",
  cancelado: "bg-red-600/25 text-red-200 border border-red-500/40",
};

export default function Rentabilidad() {
  const nav = useNavigate();
  const [data, setData] = useState({ rows: [], totales: {} });
  const [loading, setLoading] = useState(true);
  const [desde, setDesde] = useState("");
  const [hasta, setHasta] = useState("");
  const [estado, setEstado] = useState("_all");
  const [sinFacturar, setSinFacturar] = useState(false);
  const [sortBy, setSortBy] = useState("margen");
  const [sortDir, setSortDir] = useState("desc");
  const [search, setSearch] = useState("");
  const [marcando, setMarcando] = useState(null); // peticion_id en proceso

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (desde) params.desde = desde;
      if (hasta) params.hasta = hasta;
      if (estado && estado !== "_all") params.estado = estado;
      if (sinFacturar) params.sin_facturar = true;
      const { data: d } = await api.get("/dashboard/rentabilidad", { params });
      setData(d);
    } finally {
      setLoading(false);
    }
  };

  const toggleFacturada = async (r) => {
    setMarcando(r.peticion_id);
    try {
      if (r.facturada_at) {
        await api.post(`/dashboard/rentabilidad/${r.peticion_id}/desfacturar`);
      } else {
        const factura_numero = window.prompt("Nº de factura (opcional):", "") || null;
        await api.post(`/dashboard/rentabilidad/${r.peticion_id}/facturada`, { factura_numero });
      }
      await load();
    } catch (e) {
      // eslint-disable-next-line no-alert
      alert("Error: " + (e?.response?.data?.detail || e.message));
    } finally {
      setMarcando(null);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rows = useMemo(() => {
    let r = [...(data.rows || [])];
    if (search) {
      const s = search.toLowerCase();
      r = r.filter((x) =>
        (x.codigo || "").toLowerCase().includes(s) ||
        (x.nombre || "").toLowerCase().includes(s) ||
        (x.cliente_nombre || "").toLowerCase().includes(s)
      );
    }
    r.sort((a, b) => {
      const va = a[sortBy] ?? 0;
      const vb = b[sortBy] ?? 0;
      if (typeof va === "string") return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return r;
  }, [data.rows, search, sortBy, sortDir]);

  const setSort = (col) => {
    if (sortBy === col) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortBy(col); setSortDir("desc"); }
  };

  const exportCSV = () => {
    const headers = [
      "Código", "Cliente", "Estado", "Fecha",
      "Ingreso producción", "Ingreso montaje", "Ingreso total",
      "Coste producción", "Coste montaje", "Gastos directos", "Gastos ruta (repartido)", "Coste total",
      "Margen €", "Margen %",
    ];
    const csvRows = [headers.join(";")];
    for (const r of rows) {
      csvRows.push([
        r.codigo, r.cliente_nombre, r.estado, r.fecha_creacion?.slice(0, 10),
        r.ingreso_produccion, r.ingreso_montaje, r.ingreso,
        r.coste_produccion, r.coste_montaje, r.gastos_directos, r.gastos_ruta_repartidos, r.coste,
        r.margen, r.margen_pct,
      ].map((v) => String(v ?? "").replace(/;/g, ",")).join(";"));
    }
    const blob = new Blob(["\ufeff" + csvRows.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rentabilidad_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const t = data.totales || {};

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto" data-testid="page-rentabilidad">
      <PageHeader
        title="Rentabilidad"
        subtitle="P&L por petición · ingreso vs coste real con reparto de gastos de ruta"
        icon={TrendingUp}
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={exportCSV}
              data-testid="btn-export-csv"
              disabled={rows.length === 0}
              className="border-zinc-700 hover:bg-zinc-800">
              <Download className="w-4 h-4 mr-2" /> CSV
            </Button>
            <Button size="sm" onClick={load}
              data-testid="btn-refresh"
              className="bg-amber-500 hover:bg-amber-600 text-zinc-950 font-semibold">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Actualizar
            </Button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KpiCard label="Ingreso" value={fmtEUR(t.ingreso)} accent="text-emerald-300" testid="kpi-ingreso" />
        <KpiCard label="Coste" value={fmtEUR(t.coste)} accent="text-red-300" testid="kpi-coste" />
        <KpiCard
          label="Margen"
          value={fmtEUR(t.margen)}
          accent={t.margen >= 0 ? "text-amber-300" : "text-red-400"}
          testid="kpi-margen"
        />
        <KpiCard
          label="Margen %"
          value={`${t.margen_pct?.toFixed?.(1) ?? 0}%`}
          accent={t.margen_pct >= 30 ? "text-emerald-300" : t.margen_pct >= 10 ? "text-amber-300" : "text-red-300"}
          testid="kpi-margen-pct"
          extra={`${t.n_peticiones ?? 0} peticiones`}
        />
      </div>

      {/* Filtros */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-4 mb-4">
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-amber-300 font-semibold mb-3">
          <Filter className="w-3.5 h-3.5" /> Filtros
        </div>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
          <div>
            <Label className="text-[11px] text-zinc-400">Desde</Label>
            <Input type="date" value={desde} onChange={(e) => setDesde(e.target.value)}
              data-testid="input-desde"
              className="bg-zinc-900 border-zinc-800" />
          </div>
          <div>
            <Label className="text-[11px] text-zinc-400">Hasta</Label>
            <Input type="date" value={hasta} onChange={(e) => setHasta(e.target.value)}
              data-testid="input-hasta"
              className="bg-zinc-900 border-zinc-800" />
          </div>
          <div>
            <Label className="text-[11px] text-zinc-400">Estado</Label>
            <Select value={estado} onValueChange={setEstado}>
              <SelectTrigger className="bg-zinc-900 border-zinc-800" data-testid="select-estado">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-950 border-zinc-800">
                <SelectItem value="_all">Todos los estados</SelectItem>
                <SelectItem value="borrador">Borrador</SelectItem>
                <SelectItem value="presupuestada">Presupuestada</SelectItem>
                <SelectItem value="en_curso">En curso</SelectItem>
                <SelectItem value="terminado">Terminado</SelectItem>
                <SelectItem value="cancelado">Cancelado</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-1">
            <Label className="text-[11px] text-zinc-400">Buscar (código/cliente)</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              data-testid="input-search"
              placeholder="DNF-2026... o nombre"
              className="bg-zinc-900 border-zinc-800" />
          </div>
          <Button onClick={load}
            data-testid="btn-apply-filters"
            className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40">
            Aplicar
          </Button>
        </div>
        <div className="mt-3 flex items-center gap-2">
          <button
            onClick={() => { setSinFacturar(!sinFacturar); setTimeout(load, 0); }}
            data-testid="toggle-sin-facturar"
            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs uppercase tracking-wider font-semibold transition-colors ${
              sinFacturar
                ? "bg-red-500/20 text-red-200 border border-red-500/40"
                : "bg-zinc-900 text-zinc-400 border border-zinc-800 hover:bg-zinc-800"
            }`}>
            <Receipt className="w-3.5 h-3.5" />
            {sinFacturar ? "Mostrando solo pendientes de facturar" : "Solo pendientes de facturar"}
          </button>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="tabla-rentabilidad">
            <thead className="bg-zinc-950/60 text-zinc-400 text-[11px] uppercase tracking-wider">
              <tr>
                <SortableTh col="codigo" {...{ sortBy, sortDir, setSort }}>Código</SortableTh>
                <SortableTh col="cliente_nombre" {...{ sortBy, sortDir, setSort }}>Cliente</SortableTh>
                <th className="text-left p-3">Estado</th>
                <SortableTh col="ingreso" right {...{ sortBy, sortDir, setSort }}>Ingreso</SortableTh>
                <SortableTh col="coste" right {...{ sortBy, sortDir, setSort }}>Coste</SortableTh>
                <SortableTh col="margen" right {...{ sortBy, sortDir, setSort }}>Margen €</SortableTh>
                <SortableTh col="margen_pct" right {...{ sortBy, sortDir, setSort }}>Margen %</SortableTh>
                <th className="text-center p-3 text-[11px] uppercase tracking-wider">Facturar</th>
                <th className="w-12"></th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={9} className="p-8 text-center text-zinc-500 italic">
                  {loading ? "Cargando…" : "Sin peticiones en este rango"}
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.peticion_id}
                  data-testid={`row-${r.peticion_id}`}
                  className="border-t border-zinc-800/60 hover:bg-zinc-900/30">
                  <td className="p-3 font-mono text-xs text-amber-300">{r.codigo || "—"}</td>
                  <td className="p-3 text-zinc-100 max-w-[300px]">
                    <div className="truncate">{r.cliente_nombre}</div>
                    <div className="text-[10px] text-zinc-500 truncate">{r.nombre}</div>
                  </td>
                  <td className="p-3">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded ${ESTADO_COLORS[r.estado] || "bg-zinc-700 text-zinc-300"}`}>
                      {r.estado || "—"}
                    </span>
                  </td>
                  <td className="p-3 text-right">
                    <div className="text-emerald-300 font-semibold">{fmtEUR(r.ingreso)}</div>
                    <div className="text-[10px] text-zinc-500">
                      P {fmtEUR(r.ingreso_produccion)} · M {fmtEUR(r.ingreso_montaje)}
                    </div>
                  </td>
                  <td className="p-3 text-right">
                    <div className="text-red-300 font-semibold">{fmtEUR(r.coste)}</div>
                    <div className="text-[10px] text-zinc-500">
                      {r.gastos_directos > 0 ? `G ${fmtEUR(r.gastos_directos)}` : ""}
                      {r.gastos_ruta_repartidos > 0 ? ` · R ${fmtEUR(r.gastos_ruta_repartidos)}` : ""}
                      {r.combustible > 0 ? ` · ⛽ ${fmtEUR(r.combustible)}` : ""}
                    </div>
                  </td>
                  <td className={`p-3 text-right font-bold ${r.margen >= 0 ? "text-amber-300" : "text-red-400"}`}>
                    {fmtEUR(r.margen)}
                  </td>
                  <td className="p-3 text-right">
                    <div className={`inline-flex items-center gap-1 font-semibold ${
                      r.margen_pct >= 30 ? "text-emerald-300" :
                      r.margen_pct >= 10 ? "text-amber-300" :
                      r.margen_pct >= 0 ? "text-orange-300" : "text-red-400"
                    }`}>
                      {r.margen_pct >= 0
                        ? <TrendingUp className="w-3.5 h-3.5" />
                        : <TrendingDown className="w-3.5 h-3.5" />}
                      {r.margen_pct?.toFixed?.(1)}%
                    </div>
                  </td>
                  <td className="p-3 text-center">
                    <button
                      onClick={() => toggleFacturada(r)}
                      disabled={marcando === r.peticion_id}
                      data-testid={`fact-${r.peticion_id}`}
                      title={r.facturada_at
                        ? `Facturada ${r.facturada_at.slice(0, 10)}${r.factura_numero ? ` (Nº ${r.factura_numero})` : ""}\nClic para deshacer`
                        : "Marcar como facturada"}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold transition-colors disabled:opacity-50 ${
                        r.facturada_at
                          ? "bg-emerald-500/15 text-emerald-300 hover:bg-emerald-500/25"
                          : "bg-zinc-900 text-zinc-500 hover:bg-amber-500/20 hover:text-amber-200"
                      }`}>
                      {r.facturada_at
                        ? <CheckCircle2 className="w-3.5 h-3.5" />
                        : <Circle className="w-3.5 h-3.5" />}
                      {r.facturada_at
                        ? (r.factura_numero ? `#${r.factura_numero}` : "✓")
                        : "—"}
                    </button>
                  </td>
                  <td className="p-3 text-right">
                    <button onClick={() => nav(`/peticiones/${r.peticion_id}`)}
                      data-testid={`open-${r.peticion_id}`}
                      className="text-zinc-500 hover:text-amber-300">
                      <ExternalLink className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="text-[11px] text-zinc-500 mt-3 italic">
        💡 El coste incluye: producción (coste_unitario × cantidad) + montaje (coste_montaje) +
        gastos directos imputados a la petición + parte proporcional de gastos de la ruta + ⛽ combustible
        (km × consumo_L/100km × precio_diésel, configurable en <a href="/vehiculos" className="text-amber-400 underline">Vehículos</a>).
      </div>
    </div>
  );
}

function KpiCard({ label, value, accent, testid, extra }) {
  return (
    <div className="bg-zinc-900/60 border border-zinc-800 rounded-xl p-4" data-testid={testid}>
      <div className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold ${accent}`}>{value}</div>
      {extra && <div className="text-[10px] text-zinc-500 mt-1">{extra}</div>}
    </div>
  );
}

function SortableTh({ col, children, right, sortBy, sortDir, setSort }) {
  const active = sortBy === col;
  return (
    <th
      onClick={() => setSort(col)}
      className={`p-3 cursor-pointer select-none hover:text-amber-300 ${right ? "text-right" : "text-left"} ${active ? "text-amber-300" : ""}`}
    >
      <span className="inline-flex items-center gap-1">
        {children}
        <ArrowUpDown className={`w-3 h-3 ${active ? "opacity-100" : "opacity-40"}`} />
        {active && <span className="text-[10px]">{sortDir === "asc" ? "↑" : "↓"}</span>}
      </span>
    </th>
  );
}
